import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import axiosInstance from "../../api/axiosInstance";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  (window.location.hostname === "localhost"
    ? "http://localhost:5000"
    : "https://api.wheelserp.com");

const getFileUrl = (file) => {
  const filePath = file?.file_path || file?.path || file?.url;
  if (!filePath) return "";
  const path = String(filePath).replace(/\\/g, "/");
  if (/^https?:\/\//i.test(path)) return path;

  let normalizedPath;
  if (path.startsWith("/")) normalizedPath = path;
  else if (path.startsWith("uploads/")) normalizedPath = `/${path}`;
  else normalizedPath = `/uploads/${path.replace(/^\/+/, "")}`;

  return `${API_BASE_URL}${normalizedPath}`;
};

const getFileName = (file, index = 0) => {
  if (file instanceof File) return file.name;
  if (file?.originalname) return file.originalname;
  if (file?.original_name) return file.original_name;
  if (file?.filename) return file.filename;
  if (file?.name) return file.name;
  const path = file?.file_path || file?.path || "";
  return String(path).split("/").pop() || `Attachment ${index + 1}`;
};

const getAttachmentType = (file) => {
  const type = String(file?.attachment_type || "").trim();
  if (type) return type.replace(/_/g, " ");
  if (file?.is_pending) return "Amount entry";
  const name = getFileName(file).toLowerCase();
  if (name.endsWith(".pdf")) return "PDF document";
  if (/\.(jpg|jpeg|png|webp)$/i.test(name)) return "Image attachment";
  return "Attachment";
};

const getAmount = (file) => {
  if (file?.amount === null || file?.amount === undefined || file?.amount === "") return null;
  const amount = Number(file.amount);
  return Number.isFinite(amount) ? amount : null;
};

const getFileIcon = (file) => {
  const name = getFileName(file).toLowerCase();
  const mime = file?.mime_type || file?.type || "";
  if (mime === "application/pdf" || name.endsWith(".pdf")) return <PictureAsPdfOutlinedIcon />;
  if (String(mime).startsWith("image/") || /\.(jpg|jpeg|png|webp)$/i.test(name)) return <ImageOutlinedIcon />;
  return <ReceiptLongOutlinedIcon />;
};

export default function AttachmentUploader({
  attachments = [],
  setAttachments,
  clientId,
  tripsheetId,
  tripDutyId,
  onAttachmentsUploaded,
}) {
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [status, setStatus] = useState({ open: false, message: "", severity: "info" });
  const [localFiles, setLocalFiles] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewIsPdf, setPreviewIsPdf] = useState(false);
  const [previewTitle, setPreviewTitle] = useState("Attachment preview");
  const setAttachmentsRef = useRef(setAttachments);
  const MAX_FILE_SIZE_MB = 10;

  useEffect(() => {
    setAttachmentsRef.current = setAttachments;
  }, [setAttachments]);

  const showStatus = (message, severity = "info") => setStatus({ open: true, message, severity });
  const combinedAttachments = [...attachments, ...localFiles];

  const fetchAttachments = useCallback(async () => {
    if (clientId == null || tripsheetId == null || tripDutyId == null) {
      console.error("Attachment loading skipped: IDs are missing.", { clientId, tripsheetId, tripDutyId });
      return;
    }

    setFetching(true);
    try {
      const response = await axiosInstance.get("/app/duty/attachments", {
        params: { client_id: clientId, trip_duty_id: tripDutyId },
      });
      const result = response?.data;
      const candidates = [
        result,
        result?.attachments,
        result?.data,
        result?.data?.attachments,
        result?.data?.data,
        result?.data?.data?.attachments,
      ];
      const items = candidates.find(Array.isArray);
      if (!items) {
        console.error("Unexpected attachment response format:", result);
        showStatus("Could not identify attachments in the API response.", "error");
        return;
      }
      if (typeof setAttachmentsRef.current === "function") setAttachmentsRef.current(items);
    } catch (error) {
      console.error("Attachment API failed:", {
        url: error.config?.url,
        params: error.config?.params,
        status: error.response?.status,
        response: error.response?.data,
        message: error.message,
      });
      showStatus(
        error.response?.data?.message || `Unable to load attachments: ${error.response?.status || error.message}`,
        "error",
      );
    } finally {
      setFetching(false);
    }
  }, [clientId, tripsheetId, tripDutyId]);

  useEffect(() => {
    fetchAttachments();
  }, [fetchAttachments]);

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    for (const file of files) {
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        showStatus(`File "${file.name}" exceeds ${MAX_FILE_SIZE_MB} MB.`, "error");
        event.target.value = "";
        return;
      }
      if (!["application/pdf", "image/jpeg", "image/png"].includes(file.type)) {
        showStatus(`File "${file.name}" is not supported. Use PDF, JPG, or PNG.`, "error");
        event.target.value = "";
        return;
      }
    }
    setLocalFiles((previous) => [...previous, ...files]);
    event.target.value = "";
  };

  const handleDelete = (index) => {
    if (index < attachments.length) {
      // Existing attachments are removed from the current UI state only.
      // This does not delete them from the server/database.
      if (typeof setAttachments === "function") {
        setAttachments((previous) => previous.filter((_, itemIndex) => itemIndex !== index));
      }
    } else {
      const localIndex = index - attachments.length;
      setLocalFiles((previous) => previous.filter((_, itemIndex) => itemIndex !== localIndex));
    }
  };

  const handlePreview = (file) => {
    const url = file instanceof File ? URL.createObjectURL(file) : getFileUrl(file);
    if (!url) {
      showStatus("Attachment file path is missing.", "error");
      return;
    }

    const name = getFileName(file);
    const lowerName = name.toLowerCase();
    const mime = file?.mime_type || file?.type || "";
    const isPdf =
      mime === "application/pdf" ||
      lowerName.endsWith(".pdf") ||
      url.toLowerCase().split("?")[0].endsWith(".pdf");

    // Release a previous local-file preview URL before opening another one.
    if (previewUrl.startsWith("blob:")) URL.revokeObjectURL(previewUrl);

    setPreviewTitle(name);
    setPreviewIsPdf(isPdf);
    setPreviewUrl(url);
    setPreviewOpen(true);
  };

  const handleDownload = (file) => {
    const url = file instanceof File ? URL.createObjectURL(file) : getFileUrl(file);
    if (!url) {
      showStatus("Attachment file path is missing.", "error");
      return;
    }
    const link = document.createElement("a");
    link.href = url;
    link.download = getFileName(file, 0);
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    link.remove();
    if (url.startsWith("blob:")) setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    if (previewUrl.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    setPreviewUrl("");
    setPreviewIsPdf(false);
  };

  const uploadLocalFiles = async () => {
    if (!clientId || !tripsheetId || !tripDutyId) {
      showStatus("Client, tripsheet, and duty IDs are required before uploading.", "error");
      return;
    }
    if (!localFiles.length) {
      showStatus("Please select at least one attachment.", "warning");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      localFiles.forEach((file) => formData.append("files", file));
      formData.append("clientId", String(clientId));
      formData.append("tripsheetId", String(tripsheetId));
      formData.append("tripDutyId", String(tripDutyId));

      const url =
        `${API_BASE_URL}/api/tripsheets/${encodeURIComponent(clientId)}` +
        `/tripsheets/${encodeURIComponent(tripsheetId)}` +
        `/duties/${encodeURIComponent(tripDutyId)}/upload`;
      const response = await axiosInstance.post(url, formData);
      const uploadedAttachments = response?.data?.data?.attachments || response?.data?.attachments || [];

      if (Array.isArray(uploadedAttachments) && uploadedAttachments.length > 0) {
        if (typeof setAttachments === "function") {
          setAttachments((previous) => [...previous, ...uploadedAttachments]);
        }
      } else {
        await fetchAttachments();
      }
      setLocalFiles([]);
      showStatus("Attachments uploaded successfully!", "success");
      if (typeof onAttachmentsUploaded === "function") onAttachmentsUploaded(uploadedAttachments);
    } catch (error) {
      console.error("Attachment upload failed:", error.response?.data || error.message);
      showStatus(error.response?.data?.message || error.message || "Attachment upload failed.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseStatus = (_, reason) => {
    if (reason === "clickaway") return;
    setStatus((previous) => ({ ...previous, open: false }));
  };

  return (
    <>
      <Box sx={{ width: "100%", minWidth: 0 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          spacing={1.5}
          sx={{ mb: 2, p: 1.75, border: "1px solid #e3eaf2", borderRadius: 2, bgcolor: "#f8fafc" }}
        >
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#1e293b" }}>
              Duty attachments
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.25 }}>
              Parking slips, toll slips, receipts and other supporting bills
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            {(loading || fetching) && <CircularProgress size={20} />}
            <Chip
              size="small"
              icon={<AttachFileIcon />}
              label={`${combinedAttachments.length} ${combinedAttachments.length === 1 ? "file" : "files"}`}
              sx={{ bgcolor: "#e8f1fb", color: "#155e91", fontWeight: 700 }}
            />
            <Button
              variant="contained"
              component="label"
              startIcon={<AttachFileIcon />}
              disabled={loading}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5, boxShadow: "none" }}
            >
              Add attachment
              <input
                hidden
                multiple
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                onChange={handleFileChange}
              />
            </Button>
            {localFiles.length > 0 && (
              <Button
                variant="outlined"
                onClick={uploadLocalFiles}
                disabled={loading}
                sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5 }}
              >
                {loading ? "Uploading…" : `Upload ${localFiles.length}`}
              </Button>
            )}
          </Stack>
        </Stack>

        {fetching && combinedAttachments.length === 0 ? (
          <Box sx={{ py: 5, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
            <CircularProgress size={28} />
            <Typography sx={{ fontSize: 13, color: "#64748b" }}>Loading attachments…</Typography>
          </Box>
        ) : combinedAttachments.length > 0 ? (
          <List disablePadding sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {combinedAttachments.map((file, index) => {
              const name = getFileName(file, index);
              const type = getAttachmentType(file);
              const amount = getAmount(file);
              const isPending = Boolean(file?.is_pending);
              const size = file instanceof File && file.size
                ? `${(file.size / 1024 / 1024).toFixed(2)} MB`
                : null;

              return (
                <ListItem
                  key={file?.id || file?.attachment_id || file?.filename || file?.file_path || `${name}-${index}`}
                  disableGutters
                  sx={{
                    px: 1.5,
                    py: 1.25,
                    border: "1px solid #e2e8f0",
                    borderRadius: 2,
                    bgcolor: "#fff",
                    alignItems: "center",
                    transition: "border-color .15s ease, box-shadow .15s ease",
                    "&:hover": { borderColor: "#9dbbd8", boxShadow: "0 2px 8px rgba(15, 55, 90, 0.06)" },
                  }}
                  secondaryAction={
                    <Stack direction="row" spacing={0.25} alignItems="center" sx={{ position: "static", ml: 1 }}>
                      <Tooltip title="Preview attachment">
                        <IconButton size="small" onClick={() => handlePreview(file)} aria-label="Preview attachment" sx={{ color: "#386b94" }}>
                          <VisibilityOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download attachment">
                        <IconButton size="small" onClick={() => handleDownload(file)} aria-label="Download attachment" sx={{ color: "#386b94" }}>
                          <DownloadOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={index < attachments.length ? "Remove from this view" : "Remove selected file"}>
                        <IconButton size="small" onClick={() => handleDelete(index)} aria-label="Remove attachment" sx={{ color: "#c24141" }}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  }
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      flex: "0 0 44px",
                      display: "grid",
                      placeItems: "center",
                      mr: 1.5,
                      borderRadius: 1.5,
                      bgcolor: type.toLowerCase().includes("toll") ? "#fff4e5" : type.toLowerCase().includes("parking") ? "#e9f7ef" : "#edf4fb",
                      color: type.toLowerCase().includes("toll") ? "#a65c00" : type.toLowerCase().includes("parking") ? "#217346" : "#25618a",
                    }}
                  >
                    {getFileIcon(file)}
                  </Box>
                  <ListItemText
                    sx={{ my: 0, mr: { xs: 13, sm: 15 }, minWidth: 0 }}
                    primary={
                      <Typography
                        title={name}
                        sx={{ fontSize: 13.5, fontWeight: 650, color: "#1e293b", overflowWrap: "anywhere", lineHeight: 1.45 }}
                      >
                        {name}
                      </Typography>
                    }
                    secondary={
                      <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mt: 0.65 }}>
                        <Chip
                          size="small"
                          label={type}
                          sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: "#f1f5f9", color: "#475569", borderRadius: 1 }}
                        />
                        {amount !== null && (
                          <Chip
                            size="small"
                            label={`₹ ${amount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                            sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: "#eaf7ef", color: "#187245", borderRadius: 1 }}
                          />
                        )}
                        {isPending && <Chip size="small" label="Amount only" sx={{ height: 22, fontSize: 11, bgcolor: "#fff7ed", color: "#9a3412" }} />}
                        {size && <Typography component="span" sx={{ fontSize: 11, color: "#64748b" }}>{size}</Typography>}
                      </Stack>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        ) : (
          <Paper
            variant="outlined"
            sx={{ py: 4, px: 2, textAlign: "center", borderStyle: "dashed", borderColor: "#cbd5e1", bgcolor: "#fbfdff", borderRadius: 2 }}
          >
            <AttachFileIcon sx={{ fontSize: 32, color: "#94a3b8", mb: 0.5 }} />
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#334155" }}>No attachments yet</Typography>
            <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.5 }}>Add a parking slip, toll receipt or other bill for this duty.</Typography>
          </Paper>
        )}
      </Box>

      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: "96vw",
            maxWidth: "1500px",
            height: "94vh",
            maxHeight: "94vh",
            borderRadius: 2,
            overflow: "hidden",
            m: 1,
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            bgcolor: "#fff",
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 750, color: "#1e293b" }}>
              Attachment preview
            </Typography>
            <Typography
              title={previewTitle}
              sx={{ fontSize: 12, color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
              {previewTitle}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {previewUrl && (
              <Tooltip title="Open attachment in new tab">
                <IconButton
                  component="a"
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  aria-label="Open attachment in new tab"
                >
                  <VisibilityOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <IconButton onClick={handleClosePreview} size="small" aria-label="Close preview">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>
        <DialogContent
          sx={{
            p: 0,
            bgcolor: "#e9eef5",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {previewUrl && (
            previewIsPdf ? (
              <Box
                component="iframe"
                title={`PDF preview: ${previewTitle}`}
                src={previewUrl}
                sx={{ display: "block", width: "100%", height: "100%", border: 0, bgcolor: "#fff" }}
              />
            ) : (
              <Box
                component="img"
                src={previewUrl}
                alt={previewTitle || "Attachment preview"}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  display: "block",
                  mx: "auto",
                  p: { xs: 0.5, sm: 1.5 },
                  boxSizing: "border-box",
                  userSelect: "none",
                }}
                onError={() => showStatus("Unable to display the image. Check the file URL and server access.", "error")}
              />
            )
          )}
        </DialogContent>
      </Dialog>

      <Snackbar open={status.open} autoHideDuration={4000} onClose={handleCloseStatus}>
        <Alert onClose={handleCloseStatus} severity={status.severity} sx={{ width: "100%" }}>
          {status.message}
        </Alert>
      </Snackbar>
    </>
  );
}
